import type { FlacDownmixConfig } from '@mimir/common/mpegts/transmuxUtility';
import { exceptionToString } from '@mimir/common/util/exception';
import { SimpleTransmuxer } from './simpleTransmuxer';
import type { SegmentData } from './transmuxerOp';
import type { TransmuxingResult, TransmuxingWorkerOp } from './transmuxingWorkerOp';
import { ObservableState } from '@mimir/common/util/observable';


interface TransmuxerOperation {
  flacDownmixMixMatrix: number[][];
  segmentToDecode: number;
  segments: SegmentData[];
  onResponse: (result: TransmuxingResult) => void,
}


/**
 * This is a lock-free implementation that uses a queue to process one
 * transmuxing operation at a time.
 */
export class SimpleWorker implements TransmuxingWorkerOp {

  private processing = false;
  private queue: TransmuxerOperation[] = [];
  private transmuxer: SimpleTransmuxer;

  constructor(private playerId: string, audioOnly: boolean) {
    this.transmuxer = new SimpleTransmuxer(audioOnly);
  }

  post(
    flacDownmixMixMatrix: number[][],
    segmentToDecode: number,
    segments: SegmentData[],
    onResponse: (result: TransmuxingResult) => void,
  ): void {
    this.queue.push({
      flacDownmixMixMatrix,
      segmentToDecode,
      segments,
      onResponse,
    });
    this.startProcessingLoopIfNeeded();
  }

  private startProcessingLoopIfNeeded() {
    if (this.processing) {
      return;
    }

    this.processing = true;
    void this.processOperations();
  }


  private async processOperations() {
    while (this.queue.length > 0) {
      const operation = this.queue.splice(0, 1)[0];
      try {
        await this.processOperation(operation);
      } catch (error: any) {
        operation.onResponse({
          type: 'error',
          errorMsg: `Error while processing operation ${JSON.stringify(operation)}: ${exceptionToString(error)}`,
        });
      }
    }

    this.processing = false;
  }


  private async processOperation(operation: TransmuxerOperation): Promise<void> {
    const flacDownmixConfig: FlacDownmixConfig = {
      playerId: this.playerId,
      mixMatrix: operation.flacDownmixMixMatrix,
      audioChannelCount: new ObservableState<number | null>(null),
    };
    const data = await this.transmuxer.transmux(flacDownmixConfig, operation.segmentToDecode, operation.segments);
    const audioChannelCount = flacDownmixConfig.audioChannelCount.currentValue() ?? 0;
    operation.onResponse({
      type: 'success',
      data,
      audioChannelCount,
    });
  }

  cleanup() {
  }
}
